import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function AdminTitles() {
  const [pendingTitles, setPendingTitles] = useState([]);
  const [message, setMessage] = useState('');
  const [denyReasons, setDenyReasons] = useState({});
  const [loading, setLoading] = useState(true);
  const adminId = localStorage.getItem('playerId');

  useEffect(() => {
    if (!adminId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/admin-titles?adminId=${adminId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!res.ok) throw new Error(await res.text());
        setPendingTitles(await res.json() || []);
      } catch (error) {
        setMessage('Error loading titles: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [adminId]);

  const handleTitleAction = async (titleId, action) => {
    try {
      const url = action === 'approve' ? '/admin-approve-title' : '/admin-deny-title';
      const body = action === 'approve' ? { titleId } : { titleId, denyReason: denyReasons[titleId] || 'No reason provided' };
      const res = await fetch(`${API_BASE_URL}${url}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(data.message);
      setPendingTitles(pendingTitles.filter(t => t._id !== titleId));
      setDenyReasons(prev => { const { [titleId]: _, ...rest } = prev; return rest; });
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  if (!adminId) return <p style={{ textAlign: 'center', color: '#333' }}>Please log in as an admin.</p>;
  if (loading) return <p style={{ textAlign: 'center', color: '#333' }}>Loading title approvals...</p>;

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <h1 style={{ textAlign: 'center', color: '#333', marginBottom: '20px' }}>Title Approvals</h1>
      {message && <p style={{ textAlign: 'center', color: message.includes('Error') ? '#FF4444' : '#4CAF50' }}>{message}</p>}

      <h2 style={{ color: '#4CAF50', marginBottom: '15px' }}>Pending Titles</h2>
      {pendingTitles.length > 0 ? (
        pendingTitles.map(title => (
          <div key={title._id} style={{ marginBottom: '20px', padding: '15px', background: '#f9f9f9', borderRadius: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
            <h3 style={{ color: '#333', marginBottom: '10px' }}>
              {title.titleInfo?.name || 'Unknown'} ({title.titleInfo?.category || 'N/A'})
            </h3>
            <p><strong>Sport:</strong> {title.titleInfo?.sport || 'N/A'}</p>
            <p><strong>Prefix/Suffix:</strong> {title.titleInfo?.prefixSuffix || 'N/A'}</p>
            <p><strong>Dog ID:</strong> <Link to={`/dog/${title.dogId}`} style={{ color: '#4CAF50' }}>{title.dogId}</Link></p>
            <p><strong>Owner:</strong> {title.playerInfo?.username || 'Unknown'}</p>
            {title.titleInfo?.ribbonUrl && (
              <img src={title.titleInfo.ribbonUrl} alt="Ribbon" style={{ width: '50px', height: '50px', marginTop: '5px' }} />
            )}

            <h4 style={{ color: '#333', marginTop: '15px', marginBottom: '10px' }}>Title Requirements</h4>
            <ul style={{ listStyle: 'none', padding: '0' }}>
              <li>
                <strong>Allowed Breeds:</strong>{' '}
                {title.titleInfo?.requirements?.allowedBreeds?.length > 0
                  ? title.titleInfo.requirements.allowedBreeds.join(', ')
                  : 'All breeds'}
              </li>
              <li>
                <strong>Required Qualifying Scores (QS):</strong>{' '}
                {title.titleInfo?.requirements?.requiredQS ?? 0}
              </li>
              <li>
                <strong>Minimum Award Points:</strong>{' '}
                {title.titleInfo?.requirements?.minAwardPoints ?? 0}
              </li>
              <li>
                <strong>Prerequisites:</strong>{' '}
                {title.titleInfo?.requirements?.prerequisites?.length > 0
                  ? title.titleInfo.requirements.prerequisites.join(', ')
                  : 'None'}
              </li>
              <li>
                <strong>Required Items:</strong>{' '}
                {title.titleInfo?.requirements?.requiredItems?.length > 0
                  ? title.titleInfo.requirements.requiredItems
                      .map(item => `${item.itemId} (x${item.quantity})`)
                      .join(', ')
                  : 'None'}
              </li>
              <li>
                <strong>Custom Requirements:</strong>{' '}
                {title.titleInfo?.requirements?.customRequirements || 'None'}
              </li>
            </ul>

            <h4 style={{ color: '#333', marginTop: '15px', marginBottom: '10px' }}>Application Details</h4>
            <p>
              <strong>Artwork URLs:</strong>{' '}
              {title.artworkUrls?.length > 0 ? (
                title.artworkUrls.map((url, index) => (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#4CAF50', marginRight: '10px' }}
                  >
                    Artwork {index + 1}
                  </a>
                ))
              ) : (
                'None'
              )}
            </p>
            <p>
              <strong>Used Items:</strong>{' '}
              {title.usedItemNames?.length > 0 ? title.usedItemNames.join(', ') : 'None'}
            </p>
            <p>
              <strong>Effective QS:</strong> {title.effectiveQS ?? 'N/A'}
            </p>
            <p>
              <strong>Status:</strong> {title.status || 'Pending'}
            </p>
            <p>
              <strong>Date Applied:</strong>{' '}
              {title.dateEarned ? new Date(title.dateEarned).toLocaleDateString() : 'N/A'}
            </p>

            <div style={{ marginTop: '15px' }}>
              <button
                onClick={() => handleTitleAction(title._id, 'approve')}
                style={{ background: '#4CAF50', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginRight: '10px' }}
              >
                Approve
              </button>
              <button
                onClick={() => handleTitleAction(title._id, 'deny')}
                style={{ background: '#FF69B4', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '3px', marginRight: '10px' }}
              >
                Deny
              </button>
              <input
                value={denyReasons[title._id] || ''}
                onChange={e => setDenyReasons({ ...denyReasons, [title._id]: e.target.value })}
                placeholder="Reason for denial"
                style={{ padding: '5px', width: '200px' }}
              />
            </div>
          </div>
        ))
      ) : (
        <p style={{ textAlign: 'center', color: '#888' }}>No pending titles.</p>
      )}
    </div>
  );
}

export default AdminTitles;